import { useState, useEffect, useCallback, useRef } from 'react';
import { Airport, NZ_AIRPORTS_DATA } from '../../data/locations';
import { AirportStatus, FogStatusCalculator } from '../../services/FogStatusCalculator';
import { NO_DATA_COLOR } from '../../theme/colors';

export type SelectedAirports = [Airport | null, Airport | null, Airport | null];

interface UseAirportSelectionProps {
  airportStatuses: Record<number, AirportStatus>;
  dataAirports: number[];
  refreshCount: number;
}

interface UseAirportSelectionResult {
  selectedAirports: SelectedAirports;
  handleAirportClick: (airport: Airport) => void;
}

/**
 * Custom hook to manage airport selection logic
 * Handles both auto-selection based on fog severity and manual selection
 */
export function useAirportSelection({
  airportStatuses,
  dataAirports,
  refreshCount
}: UseAirportSelectionProps): UseAirportSelectionResult {
  const [selectedAirports, setSelectedAirports] = useState<SelectedAirports>([null, null, null]);
  const isManualSelection = useRef(false);
  const previousRefreshCount = useRef(0);

  // Reset manual selection flag when refresh count changes (indicates a data refresh occurred)
  useEffect(() => {
    if (refreshCount > previousRefreshCount.current) {
      isManualSelection.current = false;
      previousRefreshCount.current = refreshCount;
    }
  }, [refreshCount]);

  // Auto-select airports based on fog severity whenever data changes
  useEffect(() => {
    // Don't auto-select if user has manually selected airports (until next refresh)
    if (isManualSelection.current) return;

    // Check if we have status data for most airports (wait for chunked loading to finish)
    const airportsWithData = Object.keys(airportStatuses).filter(
      id => {
        const status = airportStatuses[Number(id)];
        return status.actualColor !== NO_DATA_COLOR || status.forecastColor !== NO_DATA_COLOR;
      }
    );

    // Wait until we have data for at least 80% of airports before auto-selecting
    // This ensures we don't select prematurely while chunks are still loading
    const hasEnoughData = dataAirports.length > 0 && airportsWithData.length >= dataAirports.length * 0.8;

    // Auto-select if we have enough data
    if (hasEnoughData) {
      // Prepare airports for ranking
      const airportsForRanking = dataAirports
        .map(airportId => {
          const airport = NZ_AIRPORTS_DATA.find(a => a.id === airportId);
          if (!airport || !airportStatuses[airportId]) return null;

          return {
            id: airport.id,
            code: airport.code,
            name: airport.name,
            status: airportStatuses[airportId]
          };
        })
        .filter(a => a !== null) as Array<{ id: number; code: string; name: string; status: AirportStatus }>;

      // Rank airports by severity then alphabetically
      const ranked = FogStatusCalculator.rankAirportsBySeverity(airportsForRanking);

      // Filter out clear airports (severity 3) and no data (severity 4)
      const airportsWithFog = ranked.filter(r => r.severity < 3);

      // Select top 3 airports
      if (airportsWithFog.length >= 3) {
        const first = NZ_AIRPORTS_DATA.find(a => a.id === airportsWithFog[0].id);
        const second = NZ_AIRPORTS_DATA.find(a => a.id === airportsWithFog[1].id);
        const third = NZ_AIRPORTS_DATA.find(a => a.id === airportsWithFog[2].id);
        if (first && second && third) {
          setSelectedAirports([first, second, third]);
        }
      } else if (airportsWithFog.length === 2) {
        // Two with fog, one without (pick next from ranked list)
        const first = NZ_AIRPORTS_DATA.find(a => a.id === airportsWithFog[0].id);
        const second = NZ_AIRPORTS_DATA.find(a => a.id === airportsWithFog[1].id);
        const third = ranked.length > 2
          ? NZ_AIRPORTS_DATA.find(a => a.id === ranked[2].id)
          : (() => {
              // Find the next best candidate from ranked that is not already selected
              const alreadySelectedIds = [airportsWithFog[0].id, airportsWithFog[1].id];
              const nextRanked = ranked.find(r => !alreadySelectedIds.includes(r.id));
              return nextRanked
                ? NZ_AIRPORTS_DATA.find(a => a.id === nextRanked.id)
                : NZ_AIRPORTS_DATA.find(a => dataAirports.includes(a.id) && a.id !== airportsWithFog[0].id && a.id !== airportsWithFog[1].id);
            })();
        setSelectedAirports([first || null, second || null, third || null]);
      } else if (airportsWithFog.length === 1) {
        // One with fog, two without (pick next from ranked list)
        const first = NZ_AIRPORTS_DATA.find(a => a.id === airportsWithFog[0].id);
        const second = ranked.length > 1
          ? NZ_AIRPORTS_DATA.find(a => a.id === ranked[1].id)
          : (() => {
              // Find the next best candidate from ranked that is not already selected
              const nextRanked = ranked.find(r => r.id !== airportsWithFog[0].id);
              return nextRanked ? NZ_AIRPORTS_DATA.find(a => a.id === nextRanked.id) : null;
            })();
        const third = ranked.length > 2
          ? NZ_AIRPORTS_DATA.find(a => a.id === ranked[2].id)
          : (() => {
              // Find the next best candidate from ranked that is not already selected
              const alreadySelectedIds = [
                airportsWithFog[0].id,
                ...(second ? [second.id] : [])
              ];
              const nextRanked = ranked.find(r => !alreadySelectedIds.includes(r.id));
              return nextRanked ? NZ_AIRPORTS_DATA.find(a => a.id === nextRanked.id) : null;
            })();
        setSelectedAirports([first || null, second || null, third || null]);
      } else {
        // No fog at any airport, select first three from ranked list (alphabetical)
        if (ranked.length >= 3) {
          const first = NZ_AIRPORTS_DATA.find(a => a.id === ranked[0].id);
          const second = NZ_AIRPORTS_DATA.find(a => a.id === ranked[1].id);
          const third = NZ_AIRPORTS_DATA.find(a => a.id === ranked[2].id);
          setSelectedAirports([first || null, second || null, third || null]);
        }
      }
    }
  }, [airportStatuses, dataAirports]);

  // Handle manual airport selection by clicking on map markers
  const handleAirportClick = useCallback((airport: Airport) => {
    // Mark as manual selection to prevent auto-selection from overriding
    isManualSelection.current = true;

    setSelectedAirports(prev => {
      // Don't update if clicking the same airport that's already at the top
      if (prev[0]?.id === airport.id) {
        return prev;
      }
      // Rotate selection: new airport goes to top, previous airports shift down
      return [airport, prev[0], prev[1]];
    });
  }, []);

  return {
    selectedAirports,
    handleAirportClick
  };
}